import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import DOMPurify from 'dompurify';
import { getTakeoverDetail, getTakeoverLeaderboard, submitTakeoverTweet } from '../services/campaignService';
import { useAuth } from '../contexts/AuthContext';
import useMediaQuery from '../hooks/useMediaQuery';
import SubmissionReceipt, { formatTimestamp } from '../components/ui/SubmissionReceipt';

function getSafeTweetUrl(value) {
  if (typeof value !== 'string' || value !== value.trim()) return null;

  try {
    const url = new URL(value);
    const allowedHosts = ['x.com', 'www.x.com', 'twitter.com', 'www.twitter.com'];
    const hasValidPath = /^\/[A-Za-z0-9_]{1,15}\/status\/\d+\/?$/.test(url.pathname);

    if (
      url.protocol !== 'https:' ||
      !allowedHosts.includes(url.hostname.toLowerCase()) ||
      url.port ||
      url.username ||
      url.password ||
      !hasValidPath
    ) {
      return null;
    }

    return url.href;
  } catch {
    return null;
  }
}

function timeRemaining(endsAt) {
  const diff = new Date(endsAt) - new Date();
  if (diff <= 0) return 'Ended';
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  if (days > 0) return `${days}d ${hours}h`;
  const mins = Math.floor((diff % 3600000) / 60000);
  return `${hours}h ${mins}m`;
}

function formatNumber(n) {
  if (n == null) return '—';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}

function formatScore(value) {
  if (value == null || value === '') return '—';
  const number = Number(value);
  if (!Number.isFinite(number)) return '—';
  return number.toFixed(1);
}

function getSubmissionEngagement(submission) {
  const metrics = submission?.metrics || {};
  return metrics.totalEngagement ??
    metrics.engagement ??
    ((metrics.likes || 0) + (metrics.retweets || 0) + (metrics.replies || 0) + (metrics.quotes || 0));
}

function hasSyncedMetrics(submission) {
  return submission?.processing
    ? !!submission.metrics?.lastSyncAt
    : !!(submission?.metrics?.lastSyncAt || submission?.score?.calculatedAt);
}

function mapLeaderboardEntry(submission) {
  return {
    id: submission._id || submission.id || submission.tweetId || submission.handle,
    handle: submission.creatorSnapshot?.username || submission.handle,
    finalScore: hasSyncedMetrics(submission) ? submission.score?.finalScore ?? submission.finalScore : null,
    impressions: hasSyncedMetrics(submission) ? submission.metrics?.impressions ?? submission.impressions : null,
    engagement: hasSyncedMetrics(submission) ? getSubmissionEngagement(submission) : null,
    rank: submission.rank ?? 0,
    rankEligible: submission.rankEligible ?? false,
    eligibilityLabel: hasSyncedMetrics(submission) ? submission.eligibilityLabel || 'Needs more than 500 views' : 'Awaiting metrics',
    isMe: submission.isMe || false
  };
}

function buildMyScore(submission) {
  if (!submission?.score || !hasSyncedMetrics(submission)) return null;

  return {
    ...submission.score,
    impressions: submission.metrics?.impressions ?? 0,
    engagement: getSubmissionEngagement(submission),
    likes: submission.metrics?.likes ?? 0,
    retweets: submission.metrics?.retweets ?? 0,
    replies: submission.metrics?.replies ?? 0,
    quotes: submission.metrics?.quotes ?? 0,
    rankEligible: submission.rankEligible ?? false,
    eligibilityLabel: submission.eligibilityLabel || 'Needs more than 500 views'
  };
}

function mergeLeaderboardEntries(currentEntries, nextEntries) {
  const byKey = new Map();
  [...currentEntries, ...nextEntries].forEach((entry) => {
    const key = entry.id || entry.handle;
    if (!key || !byKey.has(key)) {
      byKey.set(key || `${entry.handle}-${byKey.size}`, entry);
      return;
    }

    byKey.set(key, {
      ...byKey.get(key),
      ...entry,
      isMe: byKey.get(key).isMe || entry.isMe
    });
  });

  return Array.from(byKey.values());
}

function formatDescription(text) {
  if (!text) return '';
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Inline formatting
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/(?<!\*)\*([^*]+?)\*(?!\*)/g, '<em>$1</em>');
  html = html.replace(/~~(.+?)~~/g, '<del>$1</del>');
  html = html.replace(/`([^`]+?)`/g, '<code>$1</code>');

  // URLs to links
  html = html.replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>');

  // Split into blocks by double newlines
  const blocks = html.split(/\n\s*\n/);
  let result = '';

  for (const block of blocks) {
    const lines = block.split('\n');
    // Check if block is a list (all non-empty lines start with - , * , or 1. )
    const listLines = lines.filter(l => l.trim());
    const isBulletList = listLines.length > 0 && listLines.every(l => /^\s*[-*]\s/.test(l));
    const isNumberedList = listLines.length > 0 && listLines.every(l => /^\s*\d+[.)]\s/.test(l));

    if (isBulletList) {
      result += '<ul>' + listLines.map(l => `<li>${l.replace(/^\s*[-*]\s/, '')}</li>`).join('') + '</ul>';
    } else if (isNumberedList) {
      result += '<ol>' + listLines.map(l => `<li>${l.replace(/^\s*\d+[.)]\s/, '')}</li>`).join('') + '</ol>';
    } else {
      // Check for heading lines (# , ## , ###)
      const trimmed = block.trim();
      if (/^#{1,3}\s/.test(trimmed)) {
        const level = trimmed.match(/^(#{1,3})/)[1].length;
        const text = trimmed.replace(/^#{1,3}\s+/, '');
        result += `<h${level + 3}>${text}</h${level + 3}>`;
      } else if (/^---+$/.test(trimmed)) {
        result += '<hr/>';
      } else {
        result += `<p>${block.replace(/\n/g, '<br/>')}</p>`;
      }
    }
  }
  return result;
}

function getPlainDescription(text) {
  if (!text) return '';
  return text
    .replace(/#{1,3}\s+/g, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/(?<!\*)\*([^*]+?)\*(?!\*)/g, '$1')
    .replace(/`([^`]+?)`/g, '$1')
    .replace(/~~(.+?)~~/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}

function stripPaymentInfo(text) {
  if (!text) return '';
  const paymentPattern = /\b(payment|payout|compensation|budget|prize\s*pool|reward|paid|usdc|usd)\b|\$\s*\d/i;

  return text
    .split(/\n\s*\n/)
    .map(block => block
      .split('\n')
      .filter(line => !paymentPattern.test(line))
      .join('\n')
      .trim()
    )
    .filter(Boolean)
    .join('\n\n');
}

export default function TakeoverDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 767px)');
  const { token } = useAuth();

  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [tweetUrl, setTweetUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const [leaderboard, setLeaderboard] = useState([]);
  const [lbPage, setLbPage] = useState(1);
  const [lbLoading, setLbLoading] = useState(false);
  const [lbHasMore, setLbHasMore] = useState(false);
  const [lbError, setLbError] = useState(null);
  const [lbFailedPage, setLbFailedPage] = useState(1);
  const [showScoreInfo, setShowScoreInfo] = useState(false);
  const realtimeRefreshTimer = useRef(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const res = await getTakeoverDetail(id);
      const data = res.data;
      // Merge campaign fields + mySubmission/myScore/myRank to top level
      setCampaign({
        ...data.campaign,
        mySubmission: data.mySubmission,
        myScore: buildMyScore(data.mySubmission),
        myRank: data.mySubmission?.rank || null
      });
      // The paginated leaderboard owns its rows so detail refreshes cannot erase them.
    } catch {
      setError('Failed to load campaign details.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [id]);

  const loadLeaderboard = useCallback(async (page) => {
    setLbLoading(true);
    setLbError(null);
    try {
      const res = await getTakeoverLeaderboard(id, page);
      const data = res.data;
      // Backend returns { submissions, pagination: { page, total, totalPages } }
      const entries = (data?.submissions || data?.entries || []).map(mapLeaderboardEntry);
      const hasMore = data?.pagination
        ? data.pagination.page < data.pagination.totalPages
        : (data?.hasMore || false);
      if (page === 1) {
        setLeaderboard(entries);
      } else {
        setLeaderboard(prev => mergeLeaderboardEntries(prev, entries));
      }
      setLbHasMore(hasMore);
      setLbPage(page);
    } catch {
      setLbFailedPage(page);
      setLbError('The leaderboard could not be refreshed. Any previously loaded entries are still shown.');
    } finally {
      setLbLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
    loadLeaderboard(1);
  }, [load, loadLeaderboard]);

  useEffect(() => {
    if (!token || !id) return undefined;

    const socket = io(process.env.REACT_APP_SOCKET_URL || process.env.REACT_APP_API_URL || window.location.origin, {
      transports: ['polling', 'websocket'],
      path: '/socket.io',
      auth: { token },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30000,
      timeout: 35000
    });

    const joinRoom = () => {
      socket.emit('join:takeover', { campaignId: id });
    };
    const refreshLeaderboard = (event) => {
      if (event?.campaignId && event.campaignId !== id) return;
      if (realtimeRefreshTimer.current) {
        clearTimeout(realtimeRefreshTimer.current);
      }
      realtimeRefreshTimer.current = setTimeout(() => {
        load({ silent: true });
        loadLeaderboard(1);
      }, Math.floor(Math.random() * 2000));
    };

    socket.on('connect', joinRoom);
    socket.on('auth:success', joinRoom);
    socket.on('takeover:leaderboardUpdate', refreshLeaderboard);
    socket.on('takeover:submissionScored', refreshLeaderboard);
    socket.on('connect_error', (error) => {
      console.warn('[TakeoverDetailPage] socket connection failed:', error?.message || error);
    });

    return () => {
      if (realtimeRefreshTimer.current) {
        clearTimeout(realtimeRefreshTimer.current);
      }
      socket.emit('leave:takeover', { campaignId: id });
      socket.disconnect();
    };
  }, [id, load, loadLeaderboard, token]);

  const handleSubmit = async () => {
    const safeTweetUrl = getSafeTweetUrl(tweetUrl);
    if (!safeTweetUrl) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await submitTakeoverTweet(id, safeTweetUrl);
      if (res.data?.submission) {
        setCampaign(prev => ({ ...prev, mySubmission: res.data.submission, myScore: buildMyScore(res.data.submission), myRank: res.data.submission.rank || null }));
      }
      setSubmitted(true);
      setTweetUrl('');
      // Refresh campaign data to get updated submission/score
      await Promise.all([load({ silent: true }), loadLeaderboard(1)]);
    } catch (err) {
      const errorData = err?.response?.data || {};
      const message = errorData.error || errorData.message || 'Failed to submit tweet. Please try again.';
      setSubmitError(message);

      if (errorData.code === 'CAMPAIGN_POST_TARGET_REACHED') {
        setCampaign(prev => prev ? {
          ...prev,
          status: 'closed',
          completionReason: 'post_target_reached',
          postProgress: {
            ...prev.postProgress,
            acceptedPosts: prev.postProgress?.targetPosts ?? prev.postProgress?.acceptedPosts,
            reached: true
          }
        } : prev);
        await load({ silent: true });
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !campaign) return <div className="loading-state">Loading...</div>;
  if (error && !campaign) {
    return (
      <div className="error-state">
        <p>{error}</p>
        <button onClick={load}>Retry</button>
      </div>
    );
  }
  if (!campaign) return null;

  const isActive = campaign.status === 'active';
  const isTargetComplete = campaign.completionReason === 'post_target_reached';
  const timeLeft = campaign.endsAt ? timeRemaining(campaign.endsAt) : null;
  const isEnded = isTargetComplete || timeLeft === 'Ended' || !isActive || campaign.finalization?.status === 'finalized';
  const acceptedPosts = campaign.postProgress?.acceptedPosts ?? campaign.participantCount ?? 0;
  const targetPosts = campaign.postProgress?.targetPosts ?? campaign.autoStop?.targetPosts;
  const mySubmission = campaign.mySubmission;
  const hasSubmitted = submitted || !!mySubmission;
  const myScore = campaign.myScore;
  const myRank = campaign.myRank;
  const myEligibilityLabel = myScore && !myRank ? myScore.eligibilityLabel : '';
  const brickScore = myScore?.brickScore ?? null;
  const brickThreshold = campaign.brickThreshold || 100;
  const finalization = campaign.finalization || {};
  const resultsFinal = finalization.status === 'finalized' || campaign.status === 'paid';
  const sync = mySubmission?.processing || {};
  const lastSyncAt = mySubmission?.metrics?.lastSyncAt;
  const receipt = mySubmission ? {
    id: mySubmission._id || mySubmission.id,
    url: mySubmission.tweet?.url,
    submittedAt: mySubmission.tweet?.submittedAt || mySubmission.submittedAt,
    history: mySubmission.submissionHistory || []
  } : null;

  const prizeDisplay = campaign.prizePool != null
    ? `$${Number(campaign.prizePool).toLocaleString()} USDC`
    : null;

  const sectionPad = isMobile ? 24 : 32;
  const briefDescription = stripPaymentInfo(campaign.description);
  const guideItems = [
    {
      label: '1. Post',
      text: "Create an organic post based on the campaign below and make sure you're following the brief."
    },
    {
      label: '2. Submit Your Content',
      text: 'Once you posted your content on X, share the link to the post.'
    },
    {
      label: '3. Score',
      text: 'Your post competes on reach, engagement and quality. Keep it live through final review. Scores remain provisional until results are marked finalized; delayed provider updates retry automatically.'
    }
  ];
  const shortDescription = getPlainDescription(briefDescription);
  const briefIntro = shortDescription || 'Create one high-signal tweet, submit it, and compete on the leaderboard while the campaign is live.';

  return (
    <>
      <button className="brief-back" onClick={() => navigate('/campaigns?tab=takeover')}>
        &larr; Back to Campaigns
      </button>
      {error && <div role="alert" className="brief-section" style={{ padding: sectionPad }}>
        {error} Your last loaded campaign is shown. <button onClick={() => load({ silent: true })}>Refresh campaign</button>
      </div>}

      {/* Header */}
      <div className="takeover-header" style={{ padding: sectionPad }}>
        <div className="takeover-title">{campaign.title}</div>
        <div className="takeover-hero">
          <div className="takeover-hero-left">
            <div className={`takeover-card-status ${isEnded ? 'ended' : 'live'}`}>
              {!isEnded && <div className="pulse-dot" />}
              {isTargetComplete ? 'Campaign over' : isEnded ? 'Ended' : 'Live'}
            </div>
            {targetPosts != null ? (
              <div className="takeover-card-meta-item">
                Posts <strong>{acceptedPosts} / {targetPosts}</strong>
              </div>
            ) : campaign.participantCount != null && (
              <div className="takeover-card-meta-item">
                Creators <strong>{campaign.participantCount}</strong>
              </div>
            )}
          </div>
          <div className="takeover-hero-right">
            {timeLeft && !isEnded && (
              <div className="takeover-hero-block">
                <div className="takeover-hero-label">Deadline</div>
                <div className="takeover-hero-val">{timeLeft}</div>
              </div>
            )}
            {isEnded && (
              <div className="takeover-hero-block">
                <div className="takeover-hero-label">Status</div>
                <div className="takeover-hero-val" style={{ color: '#555' }}>
                  {isTargetComplete ? 'Campaign over' : 'Ended'}
                </div>
              </div>
            )}
            {prizeDisplay && (
              <div className="takeover-hero-block">
                <div className="takeover-hero-label">Prize Pool</div>
                <div className="takeover-hero-val gold">{prizeDisplay}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      <section className="brief-section" style={{ padding: sectionPad }} aria-label="Results status">
        <div className="brief-section-title">{resultsFinal ? 'Results Finalized' : isEnded ? 'Results Under Review' : 'Provisional Results'}</div>
        <p>{resultsFinal ? 'Results are locked.' : isEnded ? 'Submissions are closed. Final metrics are being reconciled before results are locked.' : 'Scores and ranks may change while metrics are collected.'}</p>
        {finalization.finalizedAt && <p>Finalized: {formatTimestamp(finalization.finalizedAt)} (your local time)</p>}
        {finalization.lastError && <p role="status">Final review is delayed. Automatic retry is scheduled.</p>}
        {campaign.endsAt && <p>Submission deadline: {formatTimestamp(campaign.endsAt)} (your local time)</p>}
      </section>

      <div className="takeover-guide">
        {guideItems.map(item => (
          <div key={item.label} className="takeover-guide-item">
            <div className="takeover-guide-label">{item.label}</div>
            <div className="takeover-guide-text">{item.text}</div>
          </div>
        ))}
      </div>

      {/* Brief Section */}
      {(briefDescription || (campaign.requirements && campaign.requirements.length > 0) || (campaign.hashtags && campaign.hashtags.length > 0) || (campaign.mentions && campaign.mentions.length > 0)) && (
        <div className="takeover-brief" style={{ marginTop: isMobile ? 14 : 2, padding: sectionPad }}>
          <div className="takeover-brief-kicker">Campaign Brief</div>
          <div className="takeover-brief-layout">
            <div className="takeover-brief-main">
              <div className="takeover-brief-title">Make the campaign impossible to miss.</div>
              <div className="takeover-brief-copy">{briefIntro}</div>
            </div>
          </div>

          {(campaign.requirements && campaign.requirements.length > 0) && (
            <div className="takeover-requirements">
              <div className="takeover-brief-kicker">What To Include</div>
              <div className="takeover-requirement-grid">
                {campaign.requirements.slice(0, 6).map((req, i) => (
                  <div className="takeover-requirement" key={i}>
                    <span>{String(i + 1).padStart(2, '0')}</span>
                    <p>{req}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {briefDescription && shortDescription.length > 180 && (
            <details className="takeover-full-brief">
              <summary>Read full brief</summary>
              <div
                className="brief-description"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(formatDescription(briefDescription))
                }}
              />
            </details>
          )}

          {((campaign.hashtags && campaign.hashtags.length > 0) || (campaign.mentions && campaign.mentions.length > 0)) && (
            <div className="takeover-tags" style={{ marginTop: 12 }}>
              {(campaign.hashtags || []).map((tag, i) => (
                <span key={i} className="takeover-tag">{tag.startsWith('#') ? tag : `#${tag}`}</span>
              ))}
              {(campaign.mentions || []).map((m, i) => (
                <span key={i} className="takeover-tag">{m.startsWith('@') ? m : `@${m}`}</span>
              ))}
            </div>
          )}
        </div>
      )}

      {isTargetComplete && (
        <div className="submit-status" style={{ marginTop: 2, marginBottom: 2, padding: sectionPad }}>
          <div className="submit-status-dot" />
          <div className="submit-status-text">
            {submitError || `Campaign over — ${acceptedPosts}${targetPosts != null ? ` / ${targetPosts}` : ''} posts received.`}
          </div>
        </div>
      )}

      <SubmissionReceipt receipt={receipt} status={resultsFinal ? 'Receipt retained · results finalized' : 'Post received'} padding={sectionPad} />
      {mySubmission && (
        <section className="brief-section metrics-status" style={{ padding: sectionPad }} aria-label="Metrics update status">
          <div className="brief-section-title">Metrics Updates</div>
          <p>{!lastSyncAt ? 'Awaiting the first successful metrics update. Your post is received; a score of zero has not been confirmed.' : sync.status === 'retrying' ? 'Metrics refresh is delayed. Last successful values are shown.' : resultsFinal ? 'Final metrics recorded.' : 'Latest successful metrics are shown. Results remain provisional.'}</p>
          {lastSyncAt && <p>Last successful update: {formatTimestamp(lastSyncAt)} (your local time)</p>}
          {sync.lastError && <p role="status">The metrics provider could not complete the update. We will retry automatically.</p>}
          {sync.nextAttemptAt && !resultsFinal && <p>Next attempt: {formatTimestamp(sync.nextAttemptAt)} (your local time)</p>}
          {mySubmission.ownership?.status === 'pending' && <p>Post ownership is awaiting verification. Your receipt is saved.</p>}
          {mySubmission.ownership?.status === 'mismatch' && <p role="status">This post belongs to a different X account and cannot receive a rank. Replace it with your own post while submissions are open.</p>}
          <button onClick={() => load({ silent: true })}>Refresh update status</button>
        </section>
      )}

      {/* Submit Section */}
      {!isEnded && (
        <div className="submit-section" style={{ padding: sectionPad }}>
          <div className="submit-title">{hasSubmitted ? 'Replace Your Tweet' : 'Submit Your Tweet'}</div>
          <div className="submit-desc">
            {hasSubmitted
              ? 'Submitted the wrong post or deleted it? Paste a new post URL to replace your entry. Your previous post metrics and score will reset.'
              : 'Paste the URL of your published tweet to join the leaderboard.'}
          </div>
          {getSafeTweetUrl(mySubmission?.tweet?.url) && (
            <div className="submit-desc">
              Current post: <a href={getSafeTweetUrl(mySubmission.tweet.url)} target="_blank" rel="noopener noreferrer">View on X</a>
            </div>
          )}
          <input
            className="submit-input"
            value={tweetUrl}
            aria-label={hasSubmitted ? 'Replacement tweet URL' : 'Tweet URL'}
            onChange={(e) => setTweetUrl(e.target.value)}
            placeholder="https://x.com/yourhandle/status/..."
          />
          {submitError && (
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: 'var(--red)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 12 }}>
              {submitError}
            </div>
          )}
          <button
            className="submit-btn"
            disabled={!getSafeTweetUrl(tweetUrl) || submitting}
            onClick={handleSubmit}
          >
            {submitting ? 'Submitting...' : hasSubmitted ? 'Replace Tweet' : 'Submit Tweet'}
          </button>
        </div>
      )}

      {/* My Score Section */}
      {hasSubmitted && myScore && (
        <div style={{ marginTop: isMobile ? 16 : 2 }}>
          <div className="brief-section" style={{ marginBottom: 0, padding: sectionPad }}>
            <div className="brief-section-title">
              My Score{myRank ? ` — Rank #${myRank}` : myEligibilityLabel ? ` — ${myEligibilityLabel}` : ''}
            </div>
          </div>
          <div className="score-cards" style={isMobile ? { gridTemplateColumns: 'repeat(2, 1fr)' } : {}}>
            <div className="score-card">
              <div className="score-card-label">Volume Score</div>
              <div className="score-card-val">{formatScore(myScore.volumeScore)}</div>
            </div>
            <div className="score-card">
              <div className="score-card-label">Quality Score</div>
              <div className="score-card-val">{formatScore(myScore.qualityScore)}</div>
            </div>
            <div className="score-card">
              <div className="score-card-label">NFT Bonus</div>
              <div className="score-card-val">{formatScore(myScore.nftBonus ?? 0)}</div>
            </div>
            <div className="score-card primary">
              <div className="score-card-label">{resultsFinal ? 'Final Score' : 'Current Score'}</div>
              <div className="score-card-val">{formatScore(myScore.finalScore)}</div>
            </div>
          </div>

          {(myScore.impressions != null || myScore.engagement != null || myScore.engagementRate != null) && (
            <div className="stats" style={{ marginBottom: isMobile ? 16 : 2 }}>
              {myScore.impressions != null && (
                <div className="stat">
                  <div className="stat-label">Impressions</div>
                  <div className="stat-val">{formatNumber(myScore.impressions)}</div>
                </div>
              )}
              {myScore.engagement != null && (
                <div className="stat">
                  <div className="stat-label">Engagement</div>
                  <div className="stat-val">{formatNumber(myScore.engagement)}</div>
                </div>
              )}
              {myScore.engagementRate != null && (
                <div className="stat">
                  <div className="stat-label">Eng. Rate</div>
                  <div className="stat-val">{(myScore.engagementRate * 100).toFixed(2)}%</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Leaderboard */}
      <div style={{ marginTop: isMobile ? 18 : 2 }}>
        {lbError && <div role="alert" className="brief-section" style={{ padding: sectionPad }}>
          {lbError} <button onClick={() => loadLeaderboard(lbFailedPage)} disabled={lbLoading}>Retry leaderboard</button>
        </div>}
        <div className="lb-head-row">
          <div className="sec-head" style={{ marginTop: 0, marginBottom: 0 }}>Leaderboard</div>
          <button
            className="lb-info-toggle"
            onClick={() => setShowScoreInfo(prev => !prev)}
            title="How scoring works"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M8 7v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              <circle cx="8" cy="4.5" r="0.75" fill="currentColor"/>
            </svg>
            How Scoring Works
          </button>
        </div>
        {showScoreInfo && (
          <div className="score-info-box">
            <div className="score-info-title">Scoring Breakdown</div>
            <div className="score-info-grid">
              <div className="score-info-item">
                <div className="score-info-label">Volume Score</div>
                <div className="score-info-desc">
                  Measures reach using a logarithmic scale.<br/>
                  <span className="score-info-formula">log₁₀(impressions) × 3.0 + log₁₀(organic engagement) × 1.0</span>
                </div>
              </div>
              <div className="score-info-item">
                <div className="score-info-label">Quality Score</div>
                <div className="score-info-desc">
                  Measures organic engagement efficiency relative to impressions.<br/>
                  <span className="score-info-formula">(organic engagement ÷ impressions) × 500</span>
                </div>
              </div>
              <div className="score-info-item">
                <div className="score-info-label">NFT Bonus</div>
                <div className="score-info-desc">
                  Bricktocreator NFT holders receive a 10% bonus on their raw score.
                </div>
              </div>
              <div className="score-info-item highlight">
                <div className="score-info-label">Final Score</div>
                <div className="score-info-desc">
                  <span className="score-info-formula">Volume + Quality + NFT Bonus</span>
                </div>
              </div>
            </div>
            <div className="score-info-note">
              Organic engagement = likes + retweets + quotes + capped replies. Metrics refresh automatically; provider delays are shown with your submission.
            </div>
          </div>
        )}
        <div className="leaderboard-wrap">
        <div className="leaderboard">
          <div className="lb-header">
            <span>Rank</span>
            <span>Creator</span>
            <span>Score</span>
            <span>Impressions</span>
            <span>Engagement</span>
          </div>
          {leaderboard.map((entry, i) => {
            const rank = entry.rank || 0;
            const isRanked = rank > 0;
            const isMe = entry.isMe || false;
            return (
              <div key={entry.handle || i} className={`lb-row${isMe ? ' me' : ''}`}>
                <div className={`lb-rank${isRanked && rank <= 3 ? ' top3' : ''}${!isRanked ? ' ineligible' : ''}`}>
                  {isRanked ? rank : entry.eligibilityLabel}
                </div>
                <div className="lb-creator">
                  <div className="lb-pfp">
                    {entry.handle && (
                      <img
                        src={`https://unavatar.io/twitter/${entry.handle}`}
                        alt={entry.handle}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                  </div>
                  <span className="lb-handle">@{entry.handle || '—'}</span>
                </div>
                <div className="lb-score">{formatScore(entry.finalScore)}</div>
                <div className="lb-metric">{formatNumber(entry.impressions)}</div>
                <div className="lb-metric">{formatNumber(entry.engagement)}</div>
              </div>
            );
          })}
          {leaderboard.length === 0 && !lbLoading && !lbError && (
            <div style={{ padding: '20px', fontFamily: "'Space Mono', monospace", fontSize: 10, color: '#555', textTransform: 'uppercase', letterSpacing: '.08em', textAlign: 'center' }}>
              No entries yet.
            </div>
          )}
          {lbHasMore && (
            <div className="lb-more">
              <button onClick={() => loadLeaderboard(lbPage + 1)} disabled={lbLoading}>
                {lbLoading ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Brick Progress */}
      {brickScore !== null && (
        <div className="brick-progress">
          <div className="brick-progress-label">Brick Progress: {brickScore} / {brickThreshold}</div>
          <div className="brick-progress-bar">
            <div
              className="brick-progress-fill"
              style={{ width: `${Math.min(100, (brickScore / brickThreshold) * 100)}%` }}
            />
          </div>
          <div className="brick-progress-text">
            {brickScore >= brickThreshold ? 'Threshold reached' : `${brickThreshold - brickScore} points to threshold`}
          </div>
        </div>
      )}
    </>
  );
}
